import { useEffect, useRef } from 'react';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

let lenisInstance: Lenis | null = null;

/** The live Lenis instance, for components that need to stop/start scroll. */
export const getLenis = () => lenisInstance;

/**
 * Inertial smooth scrolling, wired so Lenis DRIVES ScrollTrigger.
 *
 * This is the critical detail: if both run their own rAF loop they fight, and
 * scrub animations jitter. Instead Lenis reports scroll to ScrollTrigger, and
 * GSAP's ticker drives Lenis — one loop for the whole site.
 *
 * Disabled entirely under prefers-reduced-motion, where native scrolling is
 * the correct behaviour.
 */
export function useLenis() {
  const raf = useRef<((time: number) => void) | null>(null);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    /*
      Skip Lenis on touch devices. Native momentum scrolling is better than
      anything we can synthesise, and running the rAF loop anyway costs frames
      on exactly the hardware least able to spare them.
    */
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    if (coarse) return;

    /*
      duration 0.9 with a slightly softer easing exponent. At 1.1 the scroll
      kept gliding after the wheel stopped, which reads as lag rather than
      smoothness — the page felt like it was catching up with the input.
    */
    const lenis = new Lenis({
      duration: 0.9,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -9 * t)),
      smoothWheel: true,
      wheelMultiplier: 1.1,
      // Never hijack touch scrolling: native momentum is better on mobile.
      syncTouch: false,
    });
    lenisInstance = lenis;
    document.documentElement.classList.add('lenis');

    lenis.on('scroll', ScrollTrigger.update);

    const tick = (time: number) => lenis.raf(time * 1000);
    raf.current = tick;
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    // Font swap changes element heights, which desyncs every trigger.
    document.fonts?.ready.then(() => ScrollTrigger.refresh());

    return () => {
      if (raf.current) gsap.ticker.remove(raf.current);
      lenis.destroy();
      lenisInstance = null;
      document.documentElement.classList.remove('lenis');
    };
  }, []);
}
